import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { AllocationEffectiveness, District, EffectivenessEntry, GameState, Hint, KPI, ProspectSummary, Q1Store, Q2Results, Q3Results, Q4Results, QuarterType, Resources, Store, TaerraPageInstruction } from '../interfaces/game.interface';
import { map, filter } from 'rxjs/operators';
import { RequesterService } from '../../../services/requester.service';

@Injectable({ providedIn: 'root' })
export class DataService {
  private _gameState = new BehaviorSubject<any>(null);
  private readonly STORAGE_KEY = 'gameState';

  get gameState$(): Observable<GameState | null> {
    return this._gameState.asObservable();
  }

  constructor(
    private http: HttpClient,
    private requesterService: RequesterService
  ) {}

  initialize() {
    this.getMyGame().then(res => {
      if (res) {
        this.setState(res);
      } else {
        this.http.get('assets/img/game/data.json').subscribe((initialData: GameState) => {
          this.setState(initialData);
        });
      }
    })
  }

  updateState(patch: Partial<GameState>) {
    const current = this._gameState.value;
    const newState = { ...current, ...patch };
    this.setState(newState);
  }

  setState(newState: GameState) {
    this._gameState.next(newState);
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(newState));
    this.saveMyGame(newState);
  }

  getMyGame(): Promise<any> {
    return this.requesterService.get('getMyGame')
      .then(data => {
        return data.result;
      })
      .catch(e => {
        console.log('Error getMyGame >> ', e);
      });
  }

  saveMyGame(payload: any): Promise<any> {
    return this.requesterService.post('saveMyGame', payload)
      .then(data => {
        return data.result;
      })
      .catch(e => {
        console.log('Error saveMyGame >> ', e);
      });
  }

  deleteMyGame(): Promise<any> {
    return this.requesterService.post('deleteMyGame')
      .then(data => {
        return data.result;
      })
      .catch(e => {
        console.log('Error deleteMyGame >> ', e);
      });
  }

  getGameLeaderboard(quarter: string, groupId: number): Promise<any> {
    return this.requesterService.get(`getGameLeaderboard?group_id=${groupId}&quarter=${quarter}`)
      .then(data => {
        return data.result;
      })
      .catch(e => {
        console.log('Error getGameLeaderboard >> ', e);
      });
  }

  getGameStateSnapshot(): GameState | null {
    return this._gameState.value;
  }

  getPageInstructionsObservable(): Observable<TaerraPageInstruction> {
    return this.gameState$.pipe(
      filter((state): state is GameState => state !== null),
      map(state => state.taerra_page_instructions[state.game_state]),
      filter((instructions): instructions is TaerraPageInstruction =>
        !!instructions && 'initial' in instructions && 'additional' in instructions
    ));
  }

  /**
   * Check if all 15 Existing Retailers are selected
   */
  areQuarterOneAllRetailerChosen(): boolean {
    const currentState = this._gameState.value;
    if (!currentState) return false;

    const chosenRetailers = Object.values(currentState.q1_stores)
      .filter((shop: Q1Store) =>
        shop.type === 'Existing Retailer' && shop.chosen
      ).length;

    return chosenRetailers >= 15;
  }

  /**
   * Check if all 5 New Prospects are selected
   */
  areQuarterOneAllProspectsChosen(): boolean {
    const currentState = this._gameState.value;
    if (!currentState) return false;

    const chosenProspects = Object.values(currentState.q1_stores)
      .filter((shop: Q1Store) =>
        shop.type === 'New Prospect' && shop.chosen
      ).length;

    return chosenProspects >= 5;
  }

  /**
   * Check if quarter one is fully completed
   */
  isQuarterOneFinished(): boolean {
    return this.areQuarterOneAllRetailerChosen() &&
          this.areQuarterOneAllProspectsChosen();
  }

  /**
   * Check if quarter two is fully completed
   */
  isQuarterTwoFinished(): boolean {
    const resourcesLeft$ = this.getResourceLimitsLeft();
    let resourcesLeft: Resources | null = null;
    resourcesLeft$.subscribe(res => resourcesLeft = res);
    let isFinished = true;
    Object.values(resourcesLeft).forEach(value => {
      if (value > 0) {
        isFinished = false;
      }
    });
    return isFinished;
  }

  /**
   * Check if quarter three is fully completed
   */
  isQuarterThreeFinished(): boolean {
    const game_state = this.getGameStateSnapshot();  // aam should be selected first
    if (!game_state.aam.is_aam_selected) {
      return false;
    }

    const resourcesLeft$ = this.getResourceLimitsLeft();
    let resourcesLeft: Resources | null = null;
    resourcesLeft$.subscribe(res => resourcesLeft = res);
    let isFinished = true;
    Object.values(resourcesLeft).forEach(value => {
      if (value > 0) {
        isFinished = false;
      }
    });
    return isFinished;
  }

  /**
   * Check if quarter four is fully completed
   */
  isQuarterFourFinished(): boolean {
    const resourcesLeft$ = this.getResourceLimitsLeft();
    let resourcesLeft: Resources | null = null;
    resourcesLeft$.subscribe(res => resourcesLeft = res);
    let isFinished = true;
    Object.values(resourcesLeft).forEach(value => {
      if (value > 0) {
        isFinished = false;
      }
    });
    return isFinished;
  }

  /**
   * Select individual shop with safety checks
   */
  selectShop(shopId: string): void {
    const currentState = this._gameState.value;
    if (!currentState) return;

    const shop = currentState.q1_stores[shopId];
    if (!shop) return;

    // Type-specific checks
    if (shop.type === 'Existing Retailer' && this.areQuarterOneAllRetailerChosen()) {
      console.warn('Max 15 Existing Retailers selected');
      return;
    }

    if (shop.type === 'New Prospect' && this.areQuarterOneAllProspectsChosen()) {
      console.warn('Max 5 New Prospects selected');
      return;
    }

    if (shop.blocked === true) {
      console.warn('The shop was already said to be incorrect');
      return;
    }

    // Update state
    const updatedShop = { ...shop, chosen: true };
    const newState: GameState = {
      ...currentState,
      q1_stores: {
        ...currentState.q1_stores,
        [shopId]: updatedShop
      }
    };

    this.setState(newState);
  }

  deselectShop(shopId: string): void {
    const currentState = this._gameState.value;
    if (!currentState) return;

    const shop = currentState.q1_stores[shopId];
    if (!shop) return;

    const updatedShop = { ...shop, chosen: false };
    const newState: GameState = {
      ...currentState,
      q1_stores: {
        ...currentState.q1_stores,
        [shopId]: updatedShop
      }
    };

    this.setState(newState);
  }

  updateShopResources(shopId: string, resources: Resources, quarter: QuarterType): void {
    const currentState = this._gameState.value;
    if (!currentState) return;

    const shop = currentState.stores[shopId];
    if (!shop) return;

    const newState: GameState = {
      ...currentState,
      stores: {
        ...currentState.stores,
        [shopId]: {
          ...shop,
          resources: {
            ...shop.resources,
            [quarter]: resources
          }
        }
      }
    };

    this.setState(newState);
  }

  getShopObservable(shopId: string): Observable<Q1Store> {
    return this.gameState$.pipe(
      filter((state): state is GameState => state !== null), // Type guard
      map(state => state.q1_stores[shopId]),
      filter((shop): shop is Q1Store => !!shop) // Ensure shop exists
    );
  }

  getDistrictObservable(district: string): Observable<District> {
    return this.gameState$.pipe(
      filter((state): state is GameState => state !== null), // Type guard
      map(state => {
        const quarter = state.game_state; // Get info for correct quarter
        return state.districts[quarter][district];
      }),
      filter((district): district is District => !!district) // Ensure district exists
    );
  }

  concludeQuarterOne(): Promise<{
    success: boolean,
    correctSelections: number,
    incorrectIds: string[]
  }> {
    return new Promise((resolve, reject) => {
      if (!this.isQuarterOneFinished()) {
        reject(new Error('Cannot conclude quarter 1 - selection requirements not met'));
        return;
      }

      const currentState = this._gameState.value;
      if (!currentState) {
        reject(new Error('No game state available'));
        return;
      }

      const shops = currentState.q1_stores;
      const incorrectShops: {id: string, score: number}[] = [];
      const unfoundShops: string[] = [];
      const updatedShops = { ...shops };

      // Identify incorrect choices
      Object.keys(shops).forEach(shopId => {
        const shop = shops[shopId];
        if (shop.chosen && !shop.correct) {
          incorrectShops.push({id: shopId, score: shop.score});
        } else if (!shop.chosen && shop.correct) {
          unfoundShops.push(shopId);
        }
      });

      const incorrectShopsSorted = incorrectShops.sort((a, b) => a.score - b.score).slice(0, 3);
      const correctSelections = 20 - incorrectShops.length;

      if (incorrectShopsSorted.length <= 2) {
        if (currentState.q1_additional_hour) {  // award additional hour if needed
          const limits = {
            ...currentState.resource_limits.q2,
            time: currentState.resource_limits.q2.time + 1
          }
          this.setState({
            ...currentState,
            resource_limits: {
              ...currentState.resource_limits,
              q2: limits
            },
            game_state: 'q2'
          });
        } else {
          this.setState({...currentState, game_state: 'q2'});
        }

        resolve({
          success: true,
          correctSelections,
          incorrectIds: unfoundShops  // here they are actually correct ones (but not found by player)
        });
      } else {
        // Unselect and block top 3 incorrectly selected shops
        incorrectShopsSorted.forEach((shop) => {
          updatedShops[shop.id].chosen = false;
          updatedShops[shop.id].blocked = true;
        })
        this.setState({
          ...currentState,
          q1_additional_hour: false,  // if there was at least one unsuccessful attempt then no bonus
          q1_stores: updatedShops
        });

        resolve({
          success: false,
          correctSelections,
          incorrectIds: incorrectShopsSorted.map(shop => shop.id)
        })
      }
    });
  }

  getResourceLimitsLeft(): Observable<Resources> {
    return this.gameState$.pipe(
      filter((state): state is GameState => !!state),
      map(state => {
        // Get the resource limits
        const quarter = state.game_state;
        const limits = state.resource_limits[quarter];


        // Initialize used resources counters
        const usedResources = {
          time: 0,
          mcc: 0,
          tposm_p1: 0,
          tposm_p4: 0,
          tposm_p5: 0,
          tposm_full: 0,
          incentives: 0
        };

        Object.values(state.stores).forEach(shop => {
          usedResources.time += shop.resources[quarter].time || 0;
          usedResources.mcc += shop.resources[quarter].mcc || 0;
          usedResources.tposm_p1 += shop.resources[quarter].tposm_p1 || 0;
          usedResources.tposm_p4 += shop.resources[quarter].tposm_p4 || 0;
          usedResources.tposm_p5 += shop.resources[quarter].tposm_p5 || 0;
          usedResources.tposm_full += shop.resources[quarter].tposm_full || 0;
          usedResources.incentives += shop.resources[quarter].incentives || 0;
        });

        return {
          time: limits.time - usedResources.time,
          mcc: limits.mcc - usedResources.mcc,
          tposm_p1: limits.tposm_p1 - usedResources.tposm_p1,
          tposm_p4: limits.tposm_p4 - usedResources.tposm_p4,
          tposm_p5: limits.tposm_p5 - usedResources.tposm_p5,
          tposm_full: limits.tposm_full - usedResources.tposm_full,
          incentives: limits.incentives - usedResources.incentives
        };
      })
    );
  }

  concludeQuarterTwo(): Promise<Q2Results> {
    return new Promise((resolve, reject) => {
      let currentState = this._gameState.value;
      if (!currentState) return { registrations: 0, quality: 0, k2l: 0 };

      const stores: { [key: string]: Store } = currentState.stores;
      const storeKeys = Object.keys(stores);
      const totalStores = storeKeys.length;

      let total_reg_q2_end = 0;
      let total_quality_q2_end = 0;
      let total_k2l_q2_end = 0;

      let total_reg_q3_start = 0;
      let total_quality_q3_start = 0;
      let total_k2l_q3_start = 0;

      let next_q_inactive_count = 0;

      let allocationEffectiveness: AllocationEffectiveness = {
        "VERY HIGH": [],
        "HIGH": [],
        "MED": [],
        "LOW": []
      }

      let hints: {type: string, hint: string}[] = [];

      let storesWithAllocations: number = 0

      storeKeys.forEach(storeId => {
        const store = stores[storeId];

        // Extract Q2 resource allocations
        const resources_q2 = store.resources.q2;
        const time_allocated = resources_q2.time;
        const mcc_allocated = resources_q2.mcc;
        const tposm_full = resources_q2.tposm_full;
        const tposm_p1 = resources_q2.tposm_p1;
        const tposm_p4 = resources_q2.tposm_p4;
        const tposm_p5 = resources_q2.tposm_p5;
        const incentives_allocated = resources_q2.incentives;

        if (time_allocated || mcc_allocated || tposm_full || tposm_p1 || tposm_p4 || tposm_p5 || incentives_allocated) {
          storesWithAllocations++;
        }

        // Current store attributes
        const dm_skill = store.dm_skill;
        const dm_will = store.dm_will;
        const staff_skill = store.staff_skill;
        const staff_will = store.staff_will;
        const cc_volume = store.cc_volume;
        const npv = store.npv;
        const vape_spend = store.vape_spend;
        const tier = store.tier;
        const current_reg = store.registrations;


        // Calculate Time impact on registrations
        let reg_increase_time = 0;
        const skill_sum = dm_skill + staff_skill;
        const will_sum = dm_will + staff_will;

        if (time_allocated > 0 && skill_sum <= 5 && !(will_sum < 3 && time_allocated < 3)) {
          if (will_sum < 3) {
            reg_increase_time += 1;
          } else {
            if (will_sum < 6) {
              reg_increase_time += time_allocated + 5 - skill_sum;
            } else {
              reg_increase_time += time_allocated + 6 - skill_sum;
            }
          }
        }

        // Time impact on Store Data
        let time_dm_skill_increase = 0;
        let time_dm_will_increase = 0;
        let time_staff_skill_increase = 0;
        let time_staff_will_increase = 0;
        let time_cc_volume_increase = 0;
        let time_npv_increase = 0;
        let time_vape_spend_increase = 0;

        const calculateTimeDmStaffImpact = (
          value: number, reg_increase_time: number, time_cut_off: number
        ) => {
          if (reg_increase_time < time_cut_off) {
            return 0;
          }
          if (value > 2) {
            return 0;
          }
          if (reg_increase_time < 8) {
            return 1;
          }
          if (value > 1) {
            return 1;
          }
          return 2;
        }

        const calculateTimeEffectiveness = (
          time_allocated: number, reg_increase_time: number
        ) => {
          if (time_allocated === 0) {
            return "N/A";
          }
          if (reg_increase_time > 7 || time_allocated < 3 && reg_increase_time > 5) {
            return "VERY HIGH";
          }
          if (time_allocated < 4 && reg_increase_time > 3) {
            return "HIGH";
          }
          if (reg_increase_time < 3) {
            return "LOW";
          }
          return "MED";
        }

        time_dm_skill_increase += calculateTimeDmStaffImpact(dm_skill, reg_increase_time, 4);
        time_staff_skill_increase += calculateTimeDmStaffImpact(staff_skill, reg_increase_time, 4);
        time_dm_will_increase += calculateTimeDmStaffImpact(dm_will, reg_increase_time, 6);
        time_staff_will_increase += calculateTimeDmStaffImpact(staff_will, reg_increase_time, 6);

        const time_skill_increase = time_dm_skill_increase + time_staff_skill_increase;

        if (time_skill_increase > 0 && store.tag !== "Vape Store" && cc_volume < 2) {
          time_cc_volume_increase += 1;
        }
        if (time_skill_increase > 0 && npv < 2) {
          time_npv_increase += 1;
        }
        if (time_skill_increase > 0 && vape_spend < 2) {
          time_vape_spend_increase += 1;
        }

        // Calculate time allocation effectiveness
        let time_effectiveness = calculateTimeEffectiveness(time_allocated, reg_increase_time);
        if (time_effectiveness !== "N/A") {
          allocationEffectiveness[time_effectiveness].push(
            {
              type: 'time',
              title: time_allocated + (time_allocated === 1 ? ' hr ' : ' hrs ') + 'per week to ' + store.name
            })
        }

        if (time_allocated > 0 && skill_sum >= 5) {
          hints.push({type: "time", hint: "Time for coaching and training will make the most difference in stores where DM and/or Staff Skill is low."});
        }
        if (time_allocated > 0 && will_sum <= 2) {
          hints.push({type: "time", hint: "Where DM and Staff Will are both low, time spent coaching may have limited impact, as they're not motivated to learn."});
        }

        // Calculate TPOSM impact
        const calculateTposmEffectiveness = (
          tposm_full: number, tposm_p1: number, tposm_p4: number, tposm_p5: number, increase: number
        ) => {
          if (tposm_full === 1) {
            if (increase === 9) {
              return "VERY HIGH";
            }
            if (increase === 6) {
                return "HIGH";
            }
            if (increase === 3) {
              return "MED";
            }
            return "LOW";
          }
          if (tposm_p1 + tposm_p4 + tposm_p5 > 0) {
            if (increase === 3) {
              return "HIGH";
            }
            return "LOW";
          }
          return "N/A";
        }

        let tposm_cc_volume_increase = 0;
        let tposm_npv_increase = 0;
        let tposm_vape_spend_increase = 0;

        if (tposm_full + tposm_p1 > 0 && store.tag !== "Vape Store" && cc_volume <= 4) {
          tposm_cc_volume_increase += 1;
        }
        if (tposm_full + tposm_p4 > 0 && npv <= 4) {
          tposm_npv_increase += 1;
        }
        if (tposm_full + tposm_p5 > 0 && vape_spend <= 4) {
          tposm_vape_spend_increase += 1;
        }

        let reg_increase_tposm = (tposm_cc_volume_increase + tposm_npv_increase + tposm_vape_spend_increase) * 3;

        let tposm_effectiveness = calculateTposmEffectiveness(
          tposm_full, tposm_p1, tposm_p4, tposm_p5, reg_increase_tposm
        )

        if (tposm_effectiveness !== "N/A") {
          let tposm_type = '';
          if (tposm_p1) {
            tposm_type = 'P1 TPOSM';
          } else if (tposm_p4) {
            tposm_type = 'P4 TPOSM';
          } else if (tposm_p5) {
            tposm_type = 'P5 TPOSM';
          } else {
            tposm_type = 'Full Category TPOSM';
          }

          allocationEffectiveness[tposm_effectiveness].push(
            {
              type: 'tposm',
              title: tposm_type + ' to ' + store.name
            })
        }

        if (tposm_p1 && cc_volume === 5 || tposm_p4 && npv === 5 || tposm_p5 && vape_spend === 5 || tposm_full && cc_volume + npv + vape_spend === 15) {
          hints.push({type: "tposm", hint: "When deploying TPOSM, think about which types of sales have the most opportunity. For example, if Vape Spend is low, P5 TPOSM might be impactful, while if sales are low across the board, it could be a good opportunity to deploy Full Category TPOSM."});
        }
        if (tposm_p1 && store.tag === "Vape Store") {
          hints.push({type: "tposm", hint: "Your Vape Stores don't sell Conventional Cigarettes, so putting P1 TPOSM there won't achieve anything."});
        }


        // Calculate MCC impact
        const calculateMccEffectiveness = (
          mcc_allocated: number, increase: number
        ) => {
          if (mcc_allocated === 0) {
            return "N/A";
          }
          if (increase === 14) {
            return "VERY HIGH";
          }
          if (increase === 12) {
            return "HIGH";
          }
          if (increase === 10) {
            return "MED";
          }
          return "LOW";
        }

        let reg_increase_mcc = 0;
        if (mcc_allocated > 0) {
          if (tier === 1) {
            reg_increase_mcc = 10;
          } else if (tier === 2) {
            reg_increase_mcc = 14;
          } else if (tier === 3) {
            reg_increase_mcc = 12;
          } else {
            reg_increase_mcc = 8;
          }
        }

        let mcc_effectiveness = calculateMccEffectiveness(mcc_allocated, reg_increase_mcc);

        if (mcc_effectiveness !== "N/A") {
          allocationEffectiveness[mcc_effectiveness].push(
            {
              type: 'mcc',
              title: 'MCC to ' + store.name
            })
        }

        if (mcc_allocated && tier === 1) {
          hints.push({type: "mcc", hint: "Tier 1 Stores are normally expected to be self-sufficient, and may have less headroom for increased sales, so adding an MCC may not have as much impact as a Tier 2 or 3."});
        }
        if (mcc_allocated && tier === 4) {
          hints.push({type: "mcc", hint: "Tier 4 Stores are likely to have deeper issues than can be addressed by adding an MCC, so other resources may add more value for now."});
        }


        // Calculate Incentives impact
        const calculateIncentivesEffectiveness = (value: number) => {
          if (value === 0) {
            return "N/A";
          }
          if (value < 20) {
            return "VERY HIGH";
          }
          if (value < 40) {
            return "HIGH";
          }
          if (value < 60) {
            return "MED";
          }
          return "LOW";
        }

        const reg_increase_incentives = Math.floor(incentives_allocated / 10);
        let incentives_effectiveness = calculateIncentivesEffectiveness(incentives_allocated);

        if (incentives_effectiveness !== "N/A") {
          allocationEffectiveness[incentives_effectiveness].push(
            {
              type: 'incentives',
              title: '£' + incentives_allocated + ' incentives to ' + store.name
            })
        }

        if (incentives_allocated >= 40) {
          hints.push({type: "incentives", hint: "You've allocated " + (incentives_allocated < 50 ? "nearly half" : incentives_allocated === 50 ? "half" : "over half") + " of your monetary toolkit to one store. What impact could it have if this becomes an expectation for the DM, or if this store becomes inactive?"});
        }

        // Calculate total registration increase for this store
        const total_reg_increase = reg_increase_time + reg_increase_tposm + reg_increase_mcc + reg_increase_incentives;
        const new_reg = current_reg + total_reg_increase;

        // Calculate new quality
        const new_quality = 100 - (dm_skill + dm_will + staff_skill + staff_will + time_dm_skill_increase + time_dm_will_increase + time_staff_skill_increase + time_staff_will_increase) * 100 / 12;

        // Calculate new K2L
        const new_k2l = new_reg * (100 - new_quality) / 100;

        // Accumulate totals
        total_reg_q2_end += new_reg;
        total_quality_q2_end += new_quality;
        total_k2l_q2_end += new_k2l;

        const next_q_change_cc_volume = time_cc_volume_increase + tposm_cc_volume_increase + store.news_impact.q3.cc_volume;
        const next_q_change_npv = time_npv_increase + tposm_npv_increase + store.news_impact.q3.npv;
        const next_q_change_vape_spend = time_vape_spend_increase + tposm_vape_spend_increase + store.news_impact.q3.vape_spend;

        // New StoreStats are actual values + increase due to actions + changes from the news for the comming quarter
        const new_cc_volume = Math.min(5, Math.max(0, cc_volume + next_q_change_cc_volume));
        const new_npv = Math.min(5, Math.max(0, npv + next_q_change_npv));
        const new_vape_spend = Math.min(5, Math.max(0, vape_spend + next_q_change_vape_spend));
        const new_mca = Math.min(3, Math.max(0, store.mca + store.news_impact.q3.mca));
        const new_dm_skill = Math.min(3, Math.max(0, dm_skill + time_dm_skill_increase + store.news_impact.q3.dm_skill));
        const new_dm_will = Math.min(3, Math.max(0, dm_will + time_dm_will_increase + store.news_impact.q3.dm_will));
        const new_staff_skill = Math.min(3, Math.max(0, staff_skill + time_staff_skill_increase + store.news_impact.q3.staff_skill));
        const new_staff_will = Math.min(3, Math.max(0, staff_will + time_staff_will_increase + store.news_impact.q3.staff_will));
        const new_footfall = Math.min(3, Math.max(0, store.footfall + store.news_impact.q3.footfall));
        const new_score = new_cc_volume + new_npv + new_vape_spend + new_mca + new_dm_skill + new_dm_will + new_staff_skill + new_staff_will + new_footfall;

        let new_tier = tier;
        if (new_cc_volume + new_npv + new_vape_spend < 5) {
          new_tier = 4;
        } else {
          if (new_score >= 24) {
            new_tier = 1;
          } else if (new_score >= 15) {
            new_tier = 2;
          } else if (new_score >= 9) {
            new_tier = 3;
          } else {
            new_tier = 4;
          }
        }

        let next_q_new_reg = new_reg;
        if (store.state.q3 === "inactive") {  // if the store becomes inactive in the next quarter then it doesn't yield registrations
          next_q_new_reg = 0;
          next_q_inactive_count++;  // calculate number of inactive stores in the comming quarter
        } else if (next_q_change_cc_volume + next_q_change_npv + next_q_change_vape_spend === -1) {
          next_q_new_reg = Math.max(0, next_q_new_reg - 1);
        } else if (next_q_change_cc_volume + next_q_change_npv + next_q_change_vape_spend < -1) {
          next_q_new_reg = Math.max(0, next_q_new_reg - 2);
        }

        const next_q_new_quality = 100 - (new_dm_skill + new_dm_will + new_staff_skill + new_staff_will) * 100 / 12;
        const next_q_new_k2l = next_q_new_reg * (100 - next_q_new_quality) / 100;

        total_reg_q3_start += next_q_new_reg;
        total_quality_q3_start += store.state.q3 !== "inactive" ? next_q_new_quality : 0;
        total_k2l_q3_start += next_q_new_k2l;


        // Logging
        console.log("Store", storeId, {
          "Time impact on Registrations": reg_increase_time,
          "Time impact on DM Skill": time_dm_skill_increase,
          "Time impact on DM Will": time_dm_will_increase,
          "Time impact on Staff Skill": time_staff_skill_increase,
          "Time impact on Staff Will": time_staff_will_increase,
          "Time impact on CC Volume": time_cc_volume_increase,
          "Time impact on NPV": time_npv_increase,
          "Time impact on Vape Spend": time_vape_spend_increase,
          "TPOSM impact on Registrations": reg_increase_tposm,
          "TPOSM impact on CC Volume": tposm_cc_volume_increase,
          "TPOSM impact on NPV": tposm_npv_increase,
          "TPOSM impact on Vape Spend": tposm_vape_spend_increase,
          "MCC impact on Registrations": reg_increase_mcc,
          "Incentives impact on Registrations": reg_increase_incentives,
          "Total increase in Registrations": total_reg_increase,
          "Registrations at the end of stage": new_reg,
          "Quality acquisitions at the end of stage": new_quality,
          "K2L at the end of stage": new_k2l
        })


        // Update the store actions impact locally
        currentState = {
          ...currentState,
          stores: {
            ...currentState.stores,
            [storeId]: {
              ...currentState.stores[storeId],
              score: new_score,
              cc_volume: new_cc_volume,
              npv: new_npv,
              vape_spend: new_vape_spend,
              mca: new_mca,
              dm_skill: new_dm_skill,
              dm_will: new_dm_will,
              staff_skill: new_staff_skill,
              staff_will: new_staff_will,
              footfall: new_footfall,
              tier: new_tier,
              registrations: next_q_new_reg,
              quality: Math.round(next_q_new_quality),
              k2l: Math.round(next_q_new_k2l),
              kpi: {
                ...currentState.stores[storeId].kpi,
                q2_end: {
                  registrations: new_reg,
                  quality: Math.round(new_quality),
                  k2l: Math.round(new_k2l)
                },
                q3_start: {
                  registrations: next_q_new_reg,
                  quality: Math.round(next_q_new_quality),
                  k2l: Math.round(next_q_new_k2l)
                }
              },
              stats: {
                ...currentState.stores[storeId].stats,
                q3: {
                  cc_volume: new_cc_volume,
                  npv: new_npv,
                  vape_spend: new_vape_spend,
                  mca: new_mca,
                  dm_skill: new_dm_skill,
                  dm_will: new_dm_will,
                  staff_skill: new_staff_skill,
                  staff_will: new_staff_will,
                  footfall: new_footfall,
                  tier: new_tier
                }
              },
              actions_impact: {
                ...currentState.stores[storeId].actions_impact,
                q3: {  // increase in registrations
                  reg_increase_time,
                  reg_increase_tposm,
                  reg_increase_mcc,
                  reg_increase_incentives
                }
              }
            }
          }
        }
      });

      const average_quality_q2_end = Math.round(total_quality_q2_end / totalStores);
      const average_quality_q3_start = Math.round(total_quality_q3_start / (totalStores - next_q_inactive_count));

      if (storesWithAllocations <= 10) {
        hints.push({type: "other", hint: "Don't rely too much on a few stores - what if something happens to reduce their footfall or render them inactive? It's usually better to distribute your resources across your stores."});
      }

      const leastEffective = this.getAllocationsByPriority(allocationEffectiveness, false);
      const mostEffective = this.getAllocationsByPriority(allocationEffectiveness, true);
      const hint = this.getHintForLeastEffective(leastEffective, hints);

      const newState: GameState = {
        ...currentState,
        game_state: "q3",
        kpi: {
          ...currentState.kpi,
          q2_end: {
            registrations: total_reg_q2_end,
            quality: Math.round(average_quality_q2_end),
            k2l: Math.round(total_k2l_q2_end)
          },
          q3_start: {
            registrations: total_reg_q3_start,
            quality: Math.round(average_quality_q3_start),
            k2l: Math.round(total_k2l_q3_start)
          }
        },
        q2_summary: {leastEffective, mostEffective, hint},
        stores: {
          ...currentState.stores,
          ...currentState.prospects.q3
        }
      }

      this.setState(newState);

      console.log("Q2 end KPIs:", newState.kpi.q2_end);
      console.log("Q3 start KPIs:", newState.kpi.q3_start);

      resolve({
        kpi: {
          registrations: total_reg_q2_end,
          quality: Math.round(average_quality_q2_end),
          k2l: Math.round(total_k2l_q2_end)
        },
        leastEffective: leastEffective,
        mostEffective: mostEffective,
        hint: hint
      });
    })
  }

  getAllocationsByPriority(effectivenessData: AllocationEffectiveness, isMostEffective: boolean = false) {
    // Define the order of buckets based on whether we want most or least effective
    const bucketOrder = isMostEffective
      ? ['VERY HIGH', 'HIGH', 'MED', 'LOW']
      : ['LOW', 'MED', 'HIGH', 'VERY HIGH'];

    // Define priority order based on whether we want most or least effective
    const typePriority = isMostEffective
      ? ['time', 'tposm', 'mcc', 'incentives']
      : ['time', 'incentives', 'mcc', 'tposm'];

    const result = [];

    // Iterate through buckets in order
    for (const bucket of bucketOrder) {
      const allocations = effectivenessData[bucket] || [];

      // Group allocations by type
      const allocationsByType = {};
      allocations.forEach(allocation => {
        if (!allocationsByType[allocation.type]) {
          allocationsByType[allocation.type] = [];
        }
        allocationsByType[allocation.type].push(allocation);
      });

      // Process types in priority order
      for (const type of typePriority) {
        if (allocationsByType[type] && allocationsByType[type].length > 0) {
          // If we need more items and this type has some
          const needed = 2 - result.length;
          if (needed > 0) {
            // Shuffle to get random selection if multiple options
            const shuffled = [...allocationsByType[type]].sort(() => 0.5 - Math.random());
            const selected = shuffled.slice(0, needed);
            result.push(...selected);

            if (result.length >= 2) {
              return result.slice(0, 2); // Return exactly 2
            }
          }
        }
      }
    }

    return result.slice(0, 2); // In case there are fewer than 2 allocations total
  }

  getHintForLeastEffective(leastEffective: EffectivenessEntry[], hints: Hint[]): string {
    if (hints.length === 0) {
        return "You are doing great, no hints apply!";
    }

    // Get unique allocation types from least effective choices in order of appearance
    const allocationTypes = leastEffective.map(a => a.type);
    const uniqueTypes = Array.from(new Set(allocationTypes));

    // Find all hints that match the allocation types
    const matchingHints = hints.filter(h => uniqueTypes.includes(h.type));

    // If no matching hints, return first hint overall
    if (matchingHints.length === 0) {
        return hints[0].hint;
    }

    // Prioritize hints based on allocation type order and random selection
    const prioritizedHints: Hint[] = [];

    // Add hints in order of allocation type priority
    uniqueTypes.forEach(type => {
        prioritizedHints.push(...matchingHints.filter(h => h.type === type));
    });

    // Select random hint from prioritized list
    const randomIndex = Math.floor(Math.random() * prioritizedHints.length);
    return prioritizedHints[randomIndex].hint;
  }

  concludeQuarterThree(): Promise<Q3Results> {
    return new Promise((resolve, reject) => {
      let currentState = this._gameState.value;
      if (!currentState) return { registrations: 0, quality: 0, k2l: 0 };

      const stores: { [key: string]: Store } = currentState.stores;
      const storeKeys = Object.keys(stores);
      const totalStores = storeKeys.length;

      let total_reg_q3_end = 0;
      let total_quality_q3_end = 0;
      let total_k2l_q3_end = 0;

      let total_reg_q4_start = 0;
      let total_quality_q4_start = 0;
      let total_k2l_q4_start = 0;

      let next_q_inactive_count = 0;
      let inactive_count = 0;
      let failed_prospects_count = 0;
      let prospects_summary: ProspectSummary[] = [];
      let time_on_new_prospects = 0;

      let allocationEffectiveness: AllocationEffectiveness = {
        "VERY HIGH": [],
        "HIGH": [],
        "MED": [],
        "LOW": []
      }

      let hints: {type: string, hint: string}[] = [];

      let storesWithAllocations: number = 0

      storeKeys.forEach(storeId => {
        const store = stores[storeId];

        // Extract Q2 resource allocations
        const resources_q3 = store.resources.q3;
        const time_allocated = resources_q3.time;
        const mcc_allocated = resources_q3.mcc;
        const tposm_full = resources_q3.tposm_full;
        const tposm_p1 = resources_q3.tposm_p1;
        const tposm_p4 = resources_q3.tposm_p4;
        const tposm_p5 = resources_q3.tposm_p5;
        const incentives_allocated = resources_q3.incentives;

        if (time_allocated || mcc_allocated || tposm_full || tposm_p1 || tposm_p4 || tposm_p5 || incentives_allocated) {
          storesWithAllocations++;
        }

        // Current store attributes
        const dm_skill = store.dm_skill;
        const dm_will = store.dm_will;
        const staff_skill = store.staff_skill;
        const staff_will = store.staff_will;
        const cc_volume = store.cc_volume;
        const npv = store.npv;
        const vape_spend = store.vape_spend;
        const tier = store.tier;
        const current_reg = store.registrations;


        if (store.state.q3 !== "prospect") {
          if (store.state.q3 === "inactive") {
            inactive_count++;
          }

          // Calculate Time impact on registrations
          let reg_increase_time = 0;
          const skill_sum = dm_skill + staff_skill;
          const will_sum = dm_will + staff_will;

          if (time_allocated > 0 && skill_sum <= 5 && !(will_sum < 3 && time_allocated < 3)) {
            if (will_sum < 3) {
              reg_increase_time += 1;
            } else {
              if (will_sum < 6) {
                reg_increase_time += time_allocated + 5 - skill_sum;
              } else {
                reg_increase_time += time_allocated + 6 - skill_sum;
              }
            }
          }

          // Time impact on Store Data
          let time_dm_skill_increase = 0;
          let time_dm_will_increase = 0;
          let time_staff_skill_increase = 0;
          let time_staff_will_increase = 0;
          let time_cc_volume_increase = 0;
          let time_npv_increase = 0;
          let time_vape_spend_increase = 0;

          const calculateTimeDmStaffImpact = (
            value: number, reg_increase_time: number, time_cut_off: number
          ) => {
            if (reg_increase_time < time_cut_off) {
              return 0;
            }
            if (value > 2) {
              return 0;
            }
            if (reg_increase_time < 8) {
              return 1;
            }
            if (value > 1) {
              return 1;
            }
            return 2;
          }

          const calculateTimeEffectiveness = (
            time_allocated: number, reg_increase_time: number
          ) => {
            if (time_allocated === 0) {
              return "N/A";
            }
            if (reg_increase_time > 7 || time_allocated < 3 && reg_increase_time > 5) {
              return "VERY HIGH";
            }
            if (time_allocated < 4 && reg_increase_time > 3) {
              return "HIGH";
            }
            if (reg_increase_time < 3) {
              return "LOW";
            }
            return "MED";
          }

          time_dm_skill_increase += calculateTimeDmStaffImpact(dm_skill, reg_increase_time, 4);
          time_staff_skill_increase += calculateTimeDmStaffImpact(staff_skill, reg_increase_time, 4);
          time_dm_will_increase += calculateTimeDmStaffImpact(dm_will, reg_increase_time, 6);
          time_staff_will_increase += calculateTimeDmStaffImpact(staff_will, reg_increase_time, 6);

          const time_skill_increase = time_dm_skill_increase + time_staff_skill_increase;

          if (time_skill_increase > 0 && store.tag !== "Vape Store" && cc_volume < 2) {
            time_cc_volume_increase += 1;
          }
          if (time_skill_increase > 0 && npv < 2) {
            time_npv_increase += 1;
          }
          if (time_skill_increase > 0 && vape_spend < 2) {
            time_vape_spend_increase += 1;
          }

          // Calculate time allocation effectiveness
          let time_effectiveness = calculateTimeEffectiveness(time_allocated, reg_increase_time);
          if (time_effectiveness !== "N/A") {
            allocationEffectiveness[time_effectiveness].push(
              {
                type: 'time',
                title: time_allocated + (time_allocated === 1 ? ' hr ' : ' hrs ') + 'per week to ' + store.name
              })
          }

          // Calculate TPOSM impact
          const calculateTposmEffectiveness = (
            tposm_full: number, tposm_p1: number, tposm_p4: number, tposm_p5: number, increase: number
          ) => {
            if (tposm_full === 1) {
              if (increase === 9) {
                return "VERY HIGH";
              }
              if (increase === 6) {
                  return "HIGH";
              }
              if (increase === 3) {
                return "MED";
              }
              return "LOW";
            }
            if (tposm_p1 + tposm_p4 + tposm_p5 > 0) {
              if (increase === 3) {
                return "HIGH";
              }
              return "LOW";
            }
            return "N/A";
          }

          let tposm_cc_volume_increase = 0;
          let tposm_npv_increase = 0;
          let tposm_vape_spend_increase = 0;

          if (tposm_full + tposm_p1 > 0 && store.tag !== "Vape Store" && cc_volume <= 4) {
            tposm_cc_volume_increase += 1;
          }
          if (tposm_full + tposm_p4 > 0 && npv <= 4) {
            tposm_npv_increase += 1;
          }
          if (tposm_full + tposm_p5 > 0 && vape_spend <= 4) {
            tposm_vape_spend_increase += 1;
          }

          let reg_increase_tposm = (tposm_cc_volume_increase + tposm_npv_increase + tposm_vape_spend_increase) * 3;

          let tposm_effectiveness = calculateTposmEffectiveness(
            tposm_full, tposm_p1, tposm_p4, tposm_p5, reg_increase_tposm
          )

          if (tposm_effectiveness !== "N/A") {
            let tposm_type = '';
            if (tposm_p1) {
              tposm_type = 'P1 TPOSM';
            } else if (tposm_p4) {
              tposm_type = 'P4 TPOSM';
            } else if (tposm_p5) {
              tposm_type = 'P5 TPOSM';
            } else {
              tposm_type = 'Full Category TPOSM';
            }

            allocationEffectiveness[tposm_effectiveness].push(
              {
                type: 'tposm',
                title: tposm_type + ' to ' + store.name
              })
          }


          // Calculate MCC impact
          const calculateMccEffectiveness = (
            mcc_allocated: number, increase: number
          ) => {
            if (mcc_allocated === 0) {
              return "N/A";
            }
            if (increase === 14) {
              return "VERY HIGH";
            }
            if (increase === 12) {
              return "HIGH";
            }
            if (increase === 10) {
              return "MED";
            }
            return "LOW";
          }

          let reg_increase_mcc = 0;
          if (mcc_allocated > 0) {
            if (tier === 1) {
              reg_increase_mcc = 10;
            } else if (tier === 2) {
              reg_increase_mcc = 14;
            } else if (tier === 3) {
              reg_increase_mcc = 12;
            } else {
              reg_increase_mcc = 8;
            }
          } else {
            if (store.resources.q2.mcc > 0) {  // if mcc was allocated in q2 but not in q3, the gain from before is lost
              reg_increase_mcc -= store.actions_impact.q3.reg_increase_mcc;
            }
          }

          let mcc_effectiveness = calculateMccEffectiveness(mcc_allocated, reg_increase_mcc);

          if (mcc_effectiveness !== "N/A") {
            allocationEffectiveness[mcc_effectiveness].push(
              {
                type: 'mcc',
                title: 'MCC to ' + store.name
              })
          }


          // Calculate Incentives impact
          const calculateIncentivesEffectiveness = (value: number) => {
            if (value === 0) {
              return "N/A";
            }
            if (value < 20) {
              return "VERY HIGH";
            }
            if (value < 40) {
              return "HIGH";
            }
            if (value < 60) {
              return "MED";
            }
            return "LOW";
          }

          let reg_increase_incentives = Math.floor(incentives_allocated / 10);
          let incentives_effectiveness = calculateIncentivesEffectiveness(incentives_allocated);

          if (incentives_effectiveness !== "N/A") {
            allocationEffectiveness[incentives_effectiveness].push(
              {
                type: 'incentives',
                title: '£' + incentives_allocated + ' incentives to ' + store.name
              })
          }

          if (incentives_allocated === 0 && store.resources.q2.incentives > 0) {  // if incentives were allocated in q2 but not in q3, 50% of the gain from before is lost
            reg_increase_incentives -= Math.floor(0.5 * store.actions_impact.q3.reg_increase_incentives);
          }

          let unallocated_incentives_dm_will_change = 0;
          if (store.resources.q2.incentives - incentives_allocated >= 40) {
            unallocated_incentives_dm_will_change--;
          }

          // Hints based on choices for the current store
          if (time_allocated > 0 && skill_sum >= 5) {
            hints.push({type: "time", hint: "Time for coaching and training will make the most difference in stores where DM and/or Staff Skill is low."});
          }
          if (time_allocated > 0 && will_sum <= 2) {
            hints.push({type: "time", hint: "Where DM and Staff Will are both low, time spent coaching may have limited impact, as they're not motivated to learn."});
          }
          if (tposm_p1 && cc_volume === 5 || tposm_p4 && npv === 5 || tposm_p5 && vape_spend === 5 || tposm_full && cc_volume + npv + vape_spend === 15) {
            hints.push({type: "tposm", hint: "When deploying TPOSM, think about which types of sales have the most opportunity. For example, if Vape Spend is low, P5 TPOSM might be impactful, while if sales are low across the board, it could be a good opportunity to deploy Full Category TPOSM."});
          }
          if (tposm_p1 && store.tag === "Vape Store") {
            hints.push({type: "tposm", hint: "Your Vape Stores don't sell Conventional Cigarettes, so putting P1 TPOSM there won't achieve anything."});
          }
          if (mcc_allocated && tier === 1) {
            hints.push({type: "mcc", hint: "Tier 1 Stores are normally expected to be self-sufficient, and may have less headroom for increased sales, so adding an MCC may not have as much impact as a Tier 2 or 3."});
          }
          if (mcc_allocated && tier === 4) {
            hints.push({type: "mcc", hint: "Tier 4 Stores are likely to have deeper issues than can be addressed by adding an MCC, so other resources may add more value for now."});
          }
          if (!mcc_allocated && store.resources.q2.mcc) {
            hints.push({type: "mcc", hint: "MCCs are fantastic, but may not have a permanent impact on sales once the MCC is no longer present."});
          }
          if (incentives_allocated >= 40) {
            hints.push({type: "incentives", hint: "You've allocated " + (incentives_allocated < 50 ? "nearly half" : incentives_allocated === 50 ? "half" : "over half") + " of your monetary toolkit to one store. What impact could it have if this becomes an expectation for the DM, or if this store becomes inactive?"});
          }
          if (store.resources.q2.incentives - incentives_allocated >= 40) {
            hints.push({type: "incentives", hint: "When Incentives are reduced by a lot, it can frustrate the store's DM. That doesn't mean it's wrong to do it, but it highlights the risk of using too much of your monetary toolkit in one store."});
          }

          // Calculate total registration increase for this store
          const total_reg_increase = reg_increase_time + reg_increase_tposm + reg_increase_mcc + reg_increase_incentives;
          const new_reg = Math.max(0, current_reg + total_reg_increase);

          // Calculate new quality
          const new_quality = 100 - (dm_skill + dm_will + staff_skill + staff_will + time_dm_skill_increase + time_dm_will_increase + time_staff_skill_increase + time_staff_will_increase + unallocated_incentives_dm_will_change) * 100 / 12;

          // Calculate new K2L
          const new_k2l = new_reg * (100 - new_quality) / 100;

          // Accumulate totals
          total_reg_q3_end += new_reg;
          total_quality_q3_end += store.state.q3 !== "inactive" ? new_quality : 0;
          total_k2l_q3_end += new_k2l;

          const next_q_change_cc_volume = time_cc_volume_increase + tposm_cc_volume_increase + store.news_impact.q4.cc_volume;
          const next_q_change_npv = time_npv_increase + tposm_npv_increase + store.news_impact.q4.npv;
          const next_q_change_vape_spend = time_vape_spend_increase + tposm_vape_spend_increase + store.news_impact.q4.vape_spend;

          // New StoreStats are actual values + increase due to actions + changes from the news for the comming quarter
          const new_cc_volume = Math.min(5, Math.max(0, cc_volume + next_q_change_cc_volume));
          const new_npv = Math.min(5, Math.max(0, npv + next_q_change_npv));
          const new_vape_spend = Math.min(5, Math.max(0, vape_spend + next_q_change_vape_spend));
          const new_mca = Math.min(3, Math.max(0, store.mca + store.news_impact.q4.mca));
          const new_dm_skill = Math.min(3, Math.max(0, dm_skill + time_dm_skill_increase + store.news_impact.q4.dm_skill));
          const new_dm_will = Math.min(3, Math.max(0, dm_will + time_dm_will_increase + unallocated_incentives_dm_will_change + store.news_impact.q4.dm_will));
          const new_staff_skill = Math.min(3, Math.max(0, staff_skill + time_staff_skill_increase + store.news_impact.q4.staff_skill));
          const new_staff_will = Math.min(3, Math.max(0, staff_will + time_staff_will_increase + store.news_impact.q4.staff_will));
          const new_footfall = Math.min(3, Math.max(0, store.footfall + store.news_impact.q4.footfall));
          const new_score = new_cc_volume + new_npv + new_vape_spend + new_mca + new_dm_skill + new_dm_will + new_staff_skill + new_staff_will + new_footfall;


          let new_tier = tier;
          if (new_cc_volume + new_npv + new_vape_spend < 5) {
            new_tier = 4;
          } else {
            if (new_score >= 24) {
              new_tier = 1;
            } else if (new_score >= 15) {
              new_tier = 2;
            } else if (new_score >= 9) {
              new_tier = 3;
            } else {
              new_tier = 4;
            }
          }

          let next_q_new_reg = new_reg;
          if (store.state.q4 === "inactive") {  // if the store becomes inactive in the next quarter then it doesn't yield registrations
            next_q_new_reg = 0;
            next_q_inactive_count++;  // calculate number of inactive stores in the comming quarter
          } else if (next_q_change_cc_volume + next_q_change_npv + next_q_change_vape_spend === -1) {
            next_q_new_reg = Math.max(0, next_q_new_reg - 1);
          } else if (next_q_change_cc_volume + next_q_change_npv + next_q_change_vape_spend < -1) {
            next_q_new_reg = Math.max(0, next_q_new_reg - 2);
          }

          const next_q_new_quality = 100 - (new_dm_skill + new_dm_will + new_staff_skill + new_staff_will) * 100 / 12;
          const next_q_new_k2l = next_q_new_reg * (100 - next_q_new_quality) / 100;

          total_reg_q4_start += next_q_new_reg;
          total_quality_q4_start += store.state.q4 !== "inactive" ? next_q_new_quality : 0;
          total_k2l_q4_start += next_q_new_k2l;


          // Logging
          console.log("Store", storeId, {
            "Time impact on Registrations": reg_increase_time,
            "Time impact on DM Skill": time_dm_skill_increase,
            "Time impact on DM Will": time_dm_will_increase,
            "Time impact on Staff Skill": time_staff_skill_increase,
            "Time impact on Staff Will": time_staff_will_increase,
            "Time impact on CC Volume": time_cc_volume_increase,
            "Time impact on NPV": time_npv_increase,
            "Time impact on Vape Spend": time_vape_spend_increase,
            "TPOSM impact on Registrations": reg_increase_tposm,
            "TPOSM impact on CC Volume": tposm_cc_volume_increase,
            "TPOSM impact on NPV": tposm_npv_increase,
            "TPOSM impact on Vape Spend": tposm_vape_spend_increase,
            "MCC impact on Registrations": reg_increase_mcc,
            "Incentives impact on Registrations": reg_increase_incentives,
            "Total increase in Registrations": total_reg_increase,
            "Registrations at the end of stage": new_reg,
            "Quality acquisitions at the end of stage": new_quality,
            "K2L at the end of stage": new_k2l
          })


          // Update the store actions impact locally
          currentState = {
            ...currentState,
            stores: {
              ...currentState.stores,
              [storeId]: {
                ...currentState.stores[storeId],
                score: new_score,
                cc_volume: new_cc_volume,
                npv: new_npv,
                vape_spend: new_vape_spend,
                mca: new_mca,
                dm_skill: new_dm_skill,
                dm_will: new_dm_will,
                staff_skill: new_staff_skill,
                staff_will: new_staff_will,
                footfall: new_footfall,
                tier: new_tier,
                registrations: next_q_new_reg,
                quality: Math.round(next_q_new_quality),
                k2l: Math.round(next_q_new_k2l),
                kpi: {
                  ...currentState.stores[storeId].kpi,
                  q3_end: {
                    registrations: new_reg,
                    quality: Math.round(new_quality),
                    k2l: Math.round(new_k2l)
                  },
                  q4_start: {
                    registrations: next_q_new_reg,
                    quality: Math.round(next_q_new_quality),
                    k2l: Math.round(next_q_new_k2l)
                  }
                },
                stats: {
                  ...currentState.stores[storeId].stats,
                  q4: {
                    cc_volume: new_cc_volume,
                    npv: new_npv,
                    vape_spend: new_vape_spend,
                    mca: new_mca,
                    dm_skill: new_dm_skill,
                    dm_will: new_dm_will,
                    staff_skill: new_staff_skill,
                    staff_will: new_staff_will,
                    footfall: new_footfall,
                    tier: new_tier
                  }
                },
                actions_impact: {
                  ...currentState.stores[storeId].actions_impact,
                  q4: {  // increase in registrations
                    reg_increase_time,
                    reg_increase_tposm,
                    reg_increase_mcc,
                    reg_increase_incentives
                  }
                }
              }
            }
          }
        } else {  // different logic if the store is a prospect
          time_on_new_prospects += time_allocated;
          let end_dm_will = dm_will;
          let prospect_succeeds = false;

          if (time_allocated === 1) {
            end_dm_will = Math.min(dm_will + 1, 3);
            if (dm_will === 3) {
              prospect_succeeds = true;
            }
          } else if (time_allocated === 2) {
            end_dm_will = Math.min(dm_will + 1, 3);
            if (dm_will >= 2) {
              prospect_succeeds = true;
            }
          } else if (time_allocated >= 3) {
            end_dm_will = Math.min(dm_will + 1, 3);
            if (dm_will >= 1) {
              prospect_succeeds = true;
            }
          }

          let end_tier = tier;
          if (cc_volume + npv + vape_spend < 5) {
            end_tier = 4;
          } else {
            if (store.score >= 24) {
              end_tier = 1;
            } else if (store.score >= 15) {
              end_tier = 2;
            } else if (store.score >= 9) {
              end_tier = 3;
            } else {
              end_tier = 4;
            }
          }

          const new_reg = 10 - end_tier;
          const new_quality = 100 - (dm_skill + dm_will + staff_skill + staff_will) * 100 / 12;
          const new_k2l = new_reg * (100 - new_quality) / 100;

          const next_q_change_cc_volume = store.news_impact.q4.cc_volume;
          const next_q_change_npv = store.news_impact.q4.npv;
          const next_q_change_vape_spend = store.news_impact.q4.vape_spend;

          // New StoreStats are actual values + increase due to actions + changes from the news for the comming quarter
          const new_cc_volume = Math.min(5, Math.max(0, cc_volume + next_q_change_cc_volume));
          const new_npv = Math.min(5, Math.max(0, npv + next_q_change_npv));
          const new_vape_spend = Math.min(5, Math.max(0, vape_spend + next_q_change_vape_spend));
          const new_mca = Math.min(3, Math.max(0, store.mca + store.news_impact.q4.mca));
          const new_dm_skill = Math.min(3, Math.max(0, dm_skill + store.news_impact.q4.dm_skill));
          const new_dm_will = Math.min(3, Math.max(0, end_dm_will + store.news_impact.q4.dm_will));
          const new_staff_skill = Math.min(3, Math.max(0, staff_skill + store.news_impact.q4.staff_skill));
          const new_staff_will = Math.min(3, Math.max(0, staff_will + store.news_impact.q4.staff_will));
          const new_footfall = Math.min(3, Math.max(0, store.footfall + store.news_impact.q4.footfall));
          const new_score = new_cc_volume + new_npv + new_vape_spend + new_mca + new_dm_skill + new_dm_will + new_staff_skill + new_staff_will + new_footfall;


          let new_tier = tier;
          if (new_cc_volume + new_npv + new_vape_spend < 5) {
            new_tier = 4;
          } else {
            if (new_score >= 24) {
              new_tier = 1;
            } else if (new_score >= 15) {
              new_tier = 2;
            } else if (new_score >= 9) {
              new_tier = 3;
            } else {
              new_tier = 4;
            }
          }

          let next_q_new_reg = new_reg;
          if (next_q_change_cc_volume + next_q_change_npv + next_q_change_vape_spend === -1) {
            next_q_new_reg = Math.max(0, next_q_new_reg - 1);
          } else if (next_q_change_cc_volume + next_q_change_npv + next_q_change_vape_spend < -1) {
            next_q_new_reg = Math.max(0, next_q_new_reg - 2);
          }

          const next_q_new_quality = 100 - (new_dm_skill + new_dm_will + new_staff_skill + new_staff_will) * 100 / 12;
          const next_q_new_k2l = next_q_new_reg * (100 - next_q_new_quality) / 100;

          if (prospect_succeeds) {
            prospects_summary.push({
              id: storeId,
              name: store.name,
              success: true
            });

            // if prospect succeeds then its values are added to the average
            total_reg_q3_end += new_reg;
            total_quality_q3_end += new_quality;
            total_k2l_q3_end += new_k2l;

            total_reg_q4_start += next_q_new_reg;
            total_quality_q4_start += next_q_new_quality;
            total_k2l_q4_start += next_q_new_k2l;

            currentState = {
              ...currentState,
              stores: {
                ...currentState.stores,
                [storeId]: {
                  ...currentState.stores[storeId],
                  score: new_score,
                  cc_volume: new_cc_volume,
                  npv: new_npv,
                  vape_spend: new_vape_spend,
                  mca: new_mca,
                  dm_skill: new_dm_skill,
                  dm_will: new_dm_will,
                  staff_skill: new_staff_skill,
                  staff_will: new_staff_will,
                  footfall: new_footfall,
                  tier: new_tier,
                  registrations: next_q_new_reg,
                  quality: Math.round(next_q_new_quality),
                  k2l: Math.round(next_q_new_k2l),
                  kpi: {
                    ...currentState.stores[storeId].kpi,
                    q3_end: {
                      registrations: new_reg,
                      quality: Math.round(new_quality),
                      k2l: Math.round(new_k2l)
                    },
                    q4_start: {
                      registrations: next_q_new_reg,
                      quality: Math.round(next_q_new_quality),
                      k2l: Math.round(next_q_new_k2l)
                    }
                  },
                  stats: {
                    ...currentState.stores[storeId].stats,
                    q4: {
                      cc_volume: new_cc_volume,
                      npv: new_npv,
                      vape_spend: new_vape_spend,
                      mca: new_mca,
                      dm_skill: new_dm_skill,
                      dm_will: new_dm_will,
                      staff_skill: new_staff_skill,
                      staff_will: new_staff_will,
                      footfall: new_footfall,
                      tier: new_tier
                    }
                  },
                  state: {
                    ...currentState.stores[storeId].state,
                    q4: "active"
                  },
                  type: "Existing Retailer"
                }
              }
            }
          } else {
            failed_prospects_count++;

            prospects_summary.push({
              id: storeId,
              name: store.name,
              success: false
            });

            currentState = {
              ...currentState,
              stores: {
                ...currentState.stores,
                [storeId]: {
                  ...currentState.stores[storeId],
                  score: new_score,
                  cc_volume: new_cc_volume,
                  npv: new_npv,
                  vape_spend: new_vape_spend,
                  mca: new_mca,
                  dm_skill: new_dm_skill,
                  dm_will: new_dm_will,
                  staff_skill: new_staff_skill,
                  staff_will: new_staff_will,
                  footfall: new_footfall,
                  tier: new_tier,
                  registrations: 0,
                  quality: 0,
                  k2l: 0,
                  kpi: {
                    ...currentState.stores[storeId].kpi,
                    q3_end: {
                      registrations: 0,
                      quality: 0,
                      k2l: 0
                    },
                    q4_start: {
                      registrations: 0,
                      quality: 0,
                      k2l: 0
                    }
                  },
                  stats: {
                    ...currentState.stores[storeId].stats,
                    q4: {
                      cc_volume: new_cc_volume,
                      npv: new_npv,
                      vape_spend: new_vape_spend,
                      mca: new_mca,
                      dm_skill: new_dm_skill,
                      dm_will: new_dm_will,
                      staff_skill: new_staff_skill,
                      staff_will: new_staff_will,
                      footfall: new_footfall,
                      tier: new_tier
                    }
                  }
                }
              }
            }
          }
        }
      });

      const average_quality_q3_end = Math.round(total_quality_q3_end / (totalStores - inactive_count - failed_prospects_count));
      const average_quality_q4_start = Math.round(total_quality_q4_start / (totalStores - next_q_inactive_count - failed_prospects_count));

      if (storesWithAllocations <= 10) {
        hints.push({type: "other", hint: "Don't rely too much on a few stores - what if something happens to reduce their footfall or render them inactive? It's usually better to distribute your resources across your stores."});
      }
      if (time_on_new_prospects < 2) {
        hints.push({type: "other", hint: "Don't forget to spend some time prospecting - some of your stores have become Inactive and more may do so in future, so you need to bring in new stores to work with."});
      }

      const leastEffective = this.getAllocationsByPriority(allocationEffectiveness, false);
      const mostEffective = this.getAllocationsByPriority(allocationEffectiveness, true);
      const hint = this.getHintForLeastEffective(leastEffective, hints);

      let q4_additional_taerra_instruction = "";
      if (3 - failed_prospects_count === 1) {
        q4_additional_taerra_instruction = "The store you prospected is now Active, and you can take a look at their store data. Successfully prospecting stores also unlocks their network, recommending you to other retailers they know, so you will see more New Prospects available to prospect.";
      } else if (3 - failed_prospects_count > 1) {
        q4_additional_taerra_instruction = "The stores you prospected are now Active, and you can take a look at their store data. Successfully prospecting stores also unlocks their network, recommending you to other retailers they know, so you will see more New Prospects available to prospect.";
      } else {
        q4_additional_taerra_instruction = "The New Prospects from Q3 are still available to prospect, and one additional New Prospect is also available.";
      }

      let q4_news = currentState.news.q4;
      if (3 - failed_prospects_count > 0) {
        q4_news = [
          ...currentState.news.q4.slice(0, 1),
          "Your successful prospecting has unlocked some retailer networks, and more stores are available to prospect",
          ...currentState.news.q4.slice(1)
        ]
      }

      const newState: GameState = {
        ...currentState,
        game_state: "q4",
        taerra_page_instructions: {
          ...currentState.taerra_page_instructions,
          q4: {
            ...currentState.taerra_page_instructions.q4,
            additional: [q4_additional_taerra_instruction, ...currentState.taerra_page_instructions.q4.additional]
          }
        },
        news: {
          ...currentState.news,
          q4: q4_news
        },
        kpi: {
          ...currentState.kpi,
          q3_end: {
            registrations: total_reg_q3_end,
            quality: Math.round(average_quality_q3_end),
            k2l: Math.round(total_k2l_q3_end)
          },
          q4_start: {
            registrations: total_reg_q4_start,
            quality: Math.round(average_quality_q4_start),
            k2l: Math.round(total_k2l_q4_start)
          }
        },
        q3_summary: {leastEffective, mostEffective, hint, prospects: prospects_summary},
        stores: {
          ...currentState.stores,
          ...Object.entries(currentState.prospects.q4).reduce((acc, [storeId, store]) => {
            const prerequisiteSuccess = {
              '44': prospects_summary.find(el => el.id === '41')?.success ?? false,
              '45': prospects_summary.find(el => el.id === '41')?.success ?? false,
              '46': prospects_summary.find(el => el.id === '42')?.success ?? false,
              '47': prospects_summary.find(el => el.id === '42')?.success ?? false,
              '48': prospects_summary.find(el => el.id === '43')?.success ?? false,
              '49': true,
            }[storeId];

            if (prerequisiteSuccess) {
              acc[storeId] = store;
            }
            return acc;
          }, {})
        }
      }

      this.setState(newState);

      console.log("Q3 end KPIs:", newState.kpi.q3_end);
      console.log("Q4 start KPIs:", newState.kpi.q4_start);

      resolve({
        kpi: {
          registrations: total_reg_q3_end,
          quality: Math.round(average_quality_q3_end),
          k2l: Math.round(total_k2l_q3_end)
        },
        leastEffective: leastEffective,
        mostEffective: mostEffective,
        hint: hint,
        prospects: prospects_summary
      });
    })
  }

  concludeQuarterFour(): Promise<Q4Results> {
    return new Promise((resolve, reject) => {
      let currentState = this._gameState.value;
      if (!currentState) return { registrations: 0, quality: 0, k2l: 0 };

      const stores: { [key: string]: Store } = currentState.stores;
      const storeKeys = Object.keys(stores);
      const totalStores = storeKeys.length;

      let total_reg_q4_end = 0;
      let total_quality_q4_end = 0;
      let total_k2l_q4_end = 0;

      let inactive_count = 0;
      let failed_prospects_count = 0;
      let prospects_summary: ProspectSummary[] = [];
      let time_on_new_prospects = 0;
      let store_data_improvement_bonus = 0;

      let allocationEffectiveness: AllocationEffectiveness = {
        "VERY HIGH": [],
        "HIGH": [],
        "MED": [],
        "LOW": []
      }

      let hints: {type: string, hint: string}[] = [];

      let storesWithAllocations: number = 0

      storeKeys.forEach(storeId => {
        const store = stores[storeId];

        // Extract Q2 resource allocations
        const resources_q4 = store.resources.q4;
        const time_allocated = resources_q4.time;
        const mcc_allocated = resources_q4.mcc;
        const tposm_full = resources_q4.tposm_full;
        const tposm_p1 = resources_q4.tposm_p1;
        const tposm_p4 = resources_q4.tposm_p4;
        const tposm_p5 = resources_q4.tposm_p5;
        const incentives_allocated = resources_q4.incentives;

        if (time_allocated || mcc_allocated || tposm_full || tposm_p1 || tposm_p4 || tposm_p5 || incentives_allocated) {
          storesWithAllocations++;
        }

        // Current store attributes
        const dm_skill = store.dm_skill;
        const dm_will = store.dm_will;
        const staff_skill = store.staff_skill;
        const staff_will = store.staff_will;
        const cc_volume = store.cc_volume;
        const npv = store.npv;
        const vape_spend = store.vape_spend;
        const tier = store.tier;
        const current_reg = store.registrations;


        if (store.state.q4 !== "prospect") {
          if (store.state.q4 === "inactive") {
            inactive_count++;
          }

          // Calculate Time impact on registrations
          let reg_increase_time = 0;
          const skill_sum = dm_skill + staff_skill;
          const will_sum = dm_will + staff_will;

          if (time_allocated > 0 && skill_sum <= 5 && !(will_sum < 3 && time_allocated < 3)) {
            if (will_sum < 3) {
              reg_increase_time += 1;
            } else {
              if (will_sum < 6) {
                reg_increase_time += time_allocated + 5 - skill_sum;
              } else {
                reg_increase_time += time_allocated + 6 - skill_sum;
              }
            }
          }

          // Time impact on Store Data
          let time_dm_skill_increase = 0;
          let time_dm_will_increase = 0;
          let time_staff_skill_increase = 0;
          let time_staff_will_increase = 0;
          let time_cc_volume_increase = 0;
          let time_npv_increase = 0;
          let time_vape_spend_increase = 0;

          const calculateTimeDmStaffImpact = (
            value: number, reg_increase_time: number, time_cut_off: number
          ) => {
            if (reg_increase_time < time_cut_off) {
              return 0;
            }
            if (value > 2) {
              return 0;
            }
            if (reg_increase_time < 8) {
              return 1;
            }
            if (value > 1) {
              return 1;
            }
            return 2;
          }

          const calculateTimeEffectiveness = (
            time_allocated: number, reg_increase_time: number
          ) => {
            if (time_allocated === 0) {
              return "N/A";
            }
            if (reg_increase_time > 7 || time_allocated < 3 && reg_increase_time > 5) {
              return "VERY HIGH";
            }
            if (time_allocated < 4 && reg_increase_time > 3) {
              return "HIGH";
            }
            if (reg_increase_time < 3) {
              return "LOW";
            }
            return "MED";
          }

          time_dm_skill_increase += calculateTimeDmStaffImpact(dm_skill, reg_increase_time, 4);
          time_staff_skill_increase += calculateTimeDmStaffImpact(staff_skill, reg_increase_time, 4);
          time_dm_will_increase += calculateTimeDmStaffImpact(dm_will, reg_increase_time, 6);
          time_staff_will_increase += calculateTimeDmStaffImpact(staff_will, reg_increase_time, 6);

          const time_skill_increase = time_dm_skill_increase + time_staff_skill_increase;

          if (time_skill_increase > 0 && store.tag !== "Vape Store" && cc_volume < 2) {
            time_cc_volume_increase += 1;
          }
          if (time_skill_increase > 0 && npv < 2) {
            time_npv_increase += 1;
          }
          if (time_skill_increase > 0 && vape_spend < 2) {
            time_vape_spend_increase += 1;
          }

          // Calculate time allocation effectiveness
          let time_effectiveness = calculateTimeEffectiveness(time_allocated, reg_increase_time);
          if (time_effectiveness !== "N/A") {
            allocationEffectiveness[time_effectiveness].push(
              {
                type: 'time',
                title: time_allocated + (time_allocated === 1 ? ' hr ' : ' hrs ') + 'per week to ' + store.name
              })
          }

          // Calculate TPOSM impact
          const calculateTposmEffectiveness = (
            tposm_full: number, tposm_p1: number, tposm_p4: number, tposm_p5: number, increase: number
          ) => {
            if (tposm_full === 1) {
              if (increase === 9) {
                return "VERY HIGH";
              }
              if (increase === 6) {
                  return "HIGH";
              }
              if (increase === 3) {
                return "MED";
              }
              return "LOW";
            }
            if (tposm_p1 + tposm_p4 + tposm_p5 > 0) {
              if (increase === 3) {
                return "HIGH";
              }
              return "LOW";
            }
            return "N/A";
          }

          let tposm_cc_volume_increase = 0;
          let tposm_npv_increase = 0;
          let tposm_vape_spend_increase = 0;

          if (tposm_full + tposm_p1 > 0 && store.tag !== "Vape Store" && cc_volume <= 4) {
            tposm_cc_volume_increase += 1;
          }
          if (tposm_full + tposm_p4 > 0 && npv <= 4) {
            tposm_npv_increase += 1;
          }
          if (tposm_full + tposm_p5 > 0 && vape_spend <= 4) {
            tposm_vape_spend_increase += 1;
          }

          let reg_increase_tposm = (tposm_cc_volume_increase + tposm_npv_increase + tposm_vape_spend_increase) * 3;

          let tposm_effectiveness = calculateTposmEffectiveness(
            tposm_full, tposm_p1, tposm_p4, tposm_p5, reg_increase_tposm
          )

          if (tposm_effectiveness !== "N/A") {
            let tposm_type = '';
            if (tposm_p1) {
              tposm_type = 'P1 TPOSM';
            } else if (tposm_p4) {
              tposm_type = 'P4 TPOSM';
            } else if (tposm_p5) {
              tposm_type = 'P5 TPOSM';
            } else {
              tposm_type = 'Full Category TPOSM';
            }

            allocationEffectiveness[tposm_effectiveness].push(
              {
                type: 'tposm',
                title: tposm_type + ' to ' + store.name
              })
          }


          // Calculate MCC impact
          const calculateMccEffectiveness = (
            mcc_allocated: number, increase: number
          ) => {
            if (mcc_allocated === 0) {
              return "N/A";
            }
            if (increase === 14) {
              return "VERY HIGH";
            }
            if (increase === 12) {
              return "HIGH";
            }
            if (increase === 10) {
              return "MED";
            }
            return "LOW";
          }

          let reg_increase_mcc = 0;
          if (mcc_allocated > 0) {
            if (tier === 1) {
              reg_increase_mcc = 10;
            } else if (tier === 2) {
              reg_increase_mcc = 14;
            } else if (tier === 3) {
              reg_increase_mcc = 12;
            } else {
              reg_increase_mcc = 8;
            }
          } else {
            if (store.resources.q3.mcc > 0) {  // if mcc was allocated in q3 but not in q4, the gain from before is lost
              reg_increase_mcc -= store.actions_impact.q4.reg_increase_mcc;
            }
          }

          let mcc_effectiveness = calculateMccEffectiveness(mcc_allocated, reg_increase_mcc);

          if (mcc_effectiveness !== "N/A") {
            allocationEffectiveness[mcc_effectiveness].push(
              {
                type: 'mcc',
                title: 'MCC to ' + store.name
              })
          }


          // Calculate Incentives impact
          const calculateIncentivesEffectiveness = (value: number) => {
            if (value === 0) {
              return "N/A";
            }
            if (value < 20) {
              return "VERY HIGH";
            }
            if (value < 40) {
              return "HIGH";
            }
            if (value < 60) {
              return "MED";
            }
            return "LOW";
          }

          let reg_increase_incentives = Math.floor(incentives_allocated / 10);
          let incentives_effectiveness = calculateIncentivesEffectiveness(incentives_allocated);

          if (incentives_effectiveness !== "N/A") {
            allocationEffectiveness[incentives_effectiveness].push(
              {
                type: 'incentives',
                title: '£' + incentives_allocated + ' incentives to ' + store.name
              })
          }

          if (incentives_allocated === 0 && store.resources.q3.incentives > 0) {  // if incentives were allocated in q3 but not in q4, 50% of the gain from before is lost
            reg_increase_incentives -= Math.floor(0.5 * store.actions_impact.q4.reg_increase_incentives);
          }

          let unallocated_incentives_dm_will_change = 0;
          if (store.resources.q3.incentives - incentives_allocated >= 40) {
            unallocated_incentives_dm_will_change--;
          }

          // Hints based on choices for the current store
          if (time_allocated > 0 && skill_sum >= 5) {
            hints.push({type: "time", hint: "Time for coaching and training will make the most difference in stores where DM and/or Staff Skill is low."});
          }
          if (time_allocated > 0 && will_sum <= 2) {
            hints.push({type: "time", hint: "Where DM and Staff Will are both low, time spent coaching may have limited impact, as they're not motivated to learn."});
          }
          if (tposm_p1 && cc_volume === 5 || tposm_p4 && npv === 5 || tposm_p5 && vape_spend === 5 || tposm_full && cc_volume + npv + vape_spend === 15) {
            hints.push({type: "tposm", hint: "When deploying TPOSM, think about which types of sales have the most opportunity. For example, if Vape Spend is low, P5 TPOSM might be impactful, while if sales are low across the board, it could be a good opportunity to deploy Full Category TPOSM."});
          }
          if (tposm_p1 && store.tag === "Vape Store") {
            hints.push({type: "tposm", hint: "Your Vape Stores don't sell Conventional Cigarettes, so putting P1 TPOSM there won't achieve anything."});
          }
          if (mcc_allocated && tier === 1) {
            hints.push({type: "mcc", hint: "Tier 1 Stores are normally expected to be self-sufficient, and may have less headroom for increased sales, so adding an MCC may not have as much impact as a Tier 2 or 3."});
          }
          if (mcc_allocated && tier === 4) {
            hints.push({type: "mcc", hint: "Tier 4 Stores are likely to have deeper issues than can be addressed by adding an MCC, so other resources may add more value for now."});
          }
          if (!mcc_allocated && store.resources.q3.mcc) {
            hints.push({type: "mcc", hint: "MCCs are fantastic, but may not have a permanent impact on sales once the MCC is no longer present."});
          }
          if (incentives_allocated >= 40) {
            hints.push({type: "incentives", hint: "You've allocated " + (incentives_allocated < 50 ? "nearly half" : incentives_allocated === 50 ? "half" : "over half") + " of your monetary toolkit to one store. What impact could it have if this becomes an expectation for the DM, or if this store becomes inactive?"});
          }
          if (store.resources.q3.incentives - incentives_allocated >= 40) {
            hints.push({type: "incentives", hint: "When Incentives are reduced by a lot, it can frustrate the store's DM. That doesn't mean it's wrong to do it, but it highlights the risk of using too much of your monetary toolkit in one store."});
          }

          // Calculate total registration increase for this store
          const total_reg_increase = reg_increase_time + reg_increase_tposm + reg_increase_mcc + reg_increase_incentives;
          const new_reg = Math.max(0, current_reg + total_reg_increase);

          // Calculate new quality
          const new_quality = 100 - (dm_skill + dm_will + staff_skill + staff_will + time_dm_skill_increase + time_dm_will_increase + time_staff_skill_increase + time_staff_will_increase + unallocated_incentives_dm_will_change) * 100 / 12;

          // Calculate new K2L
          const new_k2l = new_reg * (100 - new_quality) / 100;

          // Accumulate totals
          total_reg_q4_end += new_reg;
          total_quality_q4_end += store.state.q4 !== "inactive" ? new_quality : 0;
          total_k2l_q4_end += new_k2l;

          store_data_improvement_bonus += store.state.q4 !== "inactive" ? 3 * (time_dm_skill_increase + time_dm_will_increase + time_staff_skill_increase + time_staff_will_increase + time_cc_volume_increase + time_npv_increase + time_vape_spend_increase + tposm_cc_volume_increase + tposm_npv_increase + tposm_vape_spend_increase) : 0;

          // Logging
          console.log("Store", storeId, {
            "Time impact on Registrations": reg_increase_time,
            "Time impact on DM Skill": time_dm_skill_increase,
            "Time impact on DM Will": time_dm_will_increase,
            "Time impact on Staff Skill": time_staff_skill_increase,
            "Time impact on Staff Will": time_staff_will_increase,
            "Time impact on CC Volume": time_cc_volume_increase,
            "Time impact on NPV": time_npv_increase,
            "Time impact on Vape Spend": time_vape_spend_increase,
            "TPOSM impact on Registrations": reg_increase_tposm,
            "TPOSM impact on CC Volume": tposm_cc_volume_increase,
            "TPOSM impact on NPV": tposm_npv_increase,
            "TPOSM impact on Vape Spend": tposm_vape_spend_increase,
            "MCC impact on Registrations": reg_increase_mcc,
            "Incentives impact on Registrations": reg_increase_incentives,
            "Total increase in Registrations": total_reg_increase,
            "Registrations at the end of stage": new_reg,
            "Quality acquisitions at the end of stage": new_quality,
            "K2L at the end of stage": new_k2l
          })

        } else {  // different logic if the store is a prospect
          time_on_new_prospects += time_allocated;
          let end_dm_will = dm_will;
          let prospect_succeeds = false;

          if (time_allocated === 1) {
            end_dm_will = Math.min(dm_will + 1, 3);
            if (dm_will === 3) {
              prospect_succeeds = true;
            }
          } else if (time_allocated === 2) {
            end_dm_will = Math.min(dm_will + 1, 3);
            if (dm_will >= 2) {
              prospect_succeeds = true;
            }
          } else if (time_allocated >= 3) {
            end_dm_will = Math.min(dm_will + 1, 3);
            if (dm_will >= 1) {
              prospect_succeeds = true;
            }
          }

          let end_tier = tier;
          if (cc_volume + npv + vape_spend < 5) {
            end_tier = 4;
          } else {
            if (store.score >= 24) {
              end_tier = 1;
            } else if (store.score >= 15) {
              end_tier = 2;
            } else if (store.score >= 9) {
              end_tier = 3;
            } else {
              end_tier = 4;
            }
          }

          const new_reg = 10 - end_tier;
          const new_quality = 100 - (dm_skill + dm_will + staff_skill + staff_will) * 100 / 12;
          const new_k2l = new_reg * (100 - new_quality) / 100;

          if (prospect_succeeds) {
            prospects_summary.push({
              id: storeId,
              name: store.name,
              success: true
            });

            // if prospect succeeds then its values are added to the average
            total_reg_q4_end += new_reg;
            total_quality_q4_end += new_quality;
            total_k2l_q4_end += new_k2l;

            store_data_improvement_bonus += 3 * (end_dm_will - dm_will);
          } else {
            failed_prospects_count++;

            prospects_summary.push({
              id: storeId,
              name: store.name,
              success: false
            });
          }
        }
      });

      const average_quality_q4_end = Math.round(total_quality_q4_end / (totalStores - inactive_count - failed_prospects_count));
      let finalResult = total_reg_q4_end + Math.round(total_k2l_q4_end) + currentState.kpi.q2_end.k2l + currentState.kpi.q3_end.k2l + store_data_improvement_bonus;
      console.log(finalResult, store_data_improvement_bonus);

      if (storesWithAllocations <= 10) {
        hints.push({type: "other", hint: "Don't rely too much on a few stores - what if something happens to reduce their footfall or render them inactive? It's usually better to distribute your resources across your stores."});
      }
      if (time_on_new_prospects < 2) {
        hints.push({type: "other", hint: "Don't forget to spend some time prospecting - some of your stores have become Inactive and more may do so in future, so you need to bring in new stores to work with."});
      }

      const leastEffective = this.getAllocationsByPriority(allocationEffectiveness, false);
      const mostEffective = this.getAllocationsByPriority(allocationEffectiveness, true);
      const hint = this.getHintForLeastEffective(leastEffective, hints);

      const newState: GameState = {
        ...currentState,
        kpi: {
          ...currentState.kpi,
          q4_end: {
            registrations: total_reg_q4_end,
            quality: Math.round(average_quality_q4_end),
            k2l: Math.round(total_k2l_q4_end)
          }
        },
        q4_summary: {leastEffective, mostEffective, hint, prospects: prospects_summary, finalResult},
      }

      this.setState(newState);

      console.log("Q4 end KPIs:", newState.kpi.q4_end);

      resolve({
        kpi: {
          registrations: total_reg_q4_end,
          quality: Math.round(average_quality_q4_end),
          k2l: Math.round(total_k2l_q4_end)
        },
        leastEffective: leastEffective,
        mostEffective: mostEffective,
        hint: hint,
        prospects: prospects_summary,
        finalResult
      });
    })
  }
}
